import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Avatar } from "../components/Avatar";
import { GameCard } from "../components/GameCard";
import { games } from "../lib/format";
import { fetchShared, type ShareDto } from "../lib/sharePage";

/**
 * What the recipient of a share link sees. Mounted OUTSIDE the auth gate in
 * App.tsx, so it renders for someone with no account; it holds no write
 * control and no link into the signed-in app.
 */
export default function Share({ token: legacy }: { token?: string } = {}) {
  const params = useParams();
  const token = params.token ?? legacy ?? "";
  const [dto, setDto] = useState<ShareDto | "expired" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchShared(token)
      .then((d) => {
        if (!cancelled) setDto(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const shell = (body: React.ReactNode) => (
    <div className="mx-auto grid max-w-3xl gap-4 p-4">
      <p className="text-[12.5px] text-faint">Shared link · read-only</p>
      {body}
    </div>
  );

  if (error) return shell(<p className="text-sm text-bad">{error}</p>);
  if (dto === null) return shell(<p className="text-sm text-muted">Loading…</p>);
  if (dto === "expired") {
    return shell(
      <>
        <h1 className="font-display text-3xl font-bold uppercase">Sports Tracker</h1>
        <p className="text-sm text-muted">This share link has expired or is invalid.</p>
      </>,
    );
  }
  const people = new Map(dto.people.map((p) => [p.id, p]));
  return shell(
    <>
      <div>
        <h1 className="font-display text-3xl leading-none font-bold uppercase">Games with {dto.person_name}</h1>
        <p className="mt-1 text-sm text-muted">{games(dto.events.length)} we went to together</p>
      </div>
      <div className="grid gap-2.5">
        {dto.events.map((e) => (
          <GameCard
            key={e.id}
            game={e}
            logos={dto.logos}
            venueImages={dto.venue_images}
            footer={
              e.attendee_ids.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {e.attendee_ids
                    .map((id) => people.get(id))
                    .filter((p) => !!p)
                    .map((p) => (
                      <span key={p!.id} className="inline-flex items-center gap-1.5 rounded-full bg-white/5 py-0.5 pr-2.5 pl-0.5 text-[13px]">
                        <Avatar name={p!.name} url={p!.avatar_url} />
                        {p!.name}
                      </span>
                    ))}
                </div>
              ) : undefined
            }
          />
        ))}
      </div>
    </>,
  );
}
